import { RefObject } from "react";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { HTTPRequestStatus } from "../../utils/models";
import { TaxCalculations } from "../../model/tax_calculations";
import { Refund } from "../../model/final_balance/refund";
import { RefundType } from "../../model/final_balance/refund_types";
import { defaultRefund } from "../../model/final_balance/default_refund";
import SignatureCanvas from "react-signature-canvas";
import { PaymentIntentToken } from "../../model/final_balance/payment_intent_token";
import { PayLaterRequest } from "../../model/final_balance/pay_later";
import { toFloatString, toIntString, withTwoDecimalPlaces } from "../../utils/dataConverter";
import { PaymentOption } from "../../model/final_balance/payment_option.d";

/*
 * Mix of balance, signature and payment states
 */
export interface PaymentState {
  taxCalculationsFetched: boolean;
  calculationFetchAttempts: number;
  paymentChoice: PaymentOption;
  status: HTTPRequestStatus;
  finalBalanceStatus: HTTPRequestStatus,
  paymentIntentStatus: HTTPRequestStatus,
  payLaterRequestStatus: HTTPRequestStatus,
  refundUpdateStatus: HTTPRequestStatus,
  signatureStatus: HTTPRequestStatus,
  signatureUrl: string;
  disableNext: boolean;
  alertMessage: string;
  successMessage: string;
  finalBalance: TaxCalculations;
  refund: Refund;
  paymentToken: string;
}

/// "Steps" in the final balance flow. These are the steps as marked by the 
/// custom stepper, though in reality multiple pages may map to a single step.
export enum FinalBalanceStep {
    Summary = 0,
    BalanceReview,
    Sign,
}

export enum PaymentStep {
  Pay = 0,
  Complete,
}

/// Mapping from actual route to CustomStepper step in the FinalBalance flow.
export const finalBalanceRouteMap = {
  '/balance': FinalBalanceStep.Summary,
  '/balance/review': FinalBalanceStep.BalanceReview,
  '/balance/sign': FinalBalanceStep.Sign,
} as { [key: string]: FinalBalanceStep };

export const finalBalanceRouteNames = {
  [FinalBalanceStep.Summary]: 'Tax Calculations',
  [FinalBalanceStep.BalanceReview]: 'Balance Due',
  [FinalBalanceStep.Sign]: 'Signature',
} as { [key: number]: string };

/// Mapping from actual route to CustomStepper step in the Payment flow.
export const paymentRouteMap = {
  '/payment/balance': PaymentStep.Pay,
  '/payment/refund': PaymentStep.Pay,
  '/payment/complete': PaymentStep.Complete,
} as { [key: string]: PaymentStep };

export const emptyTaxCalculations: TaxCalculations = {
    total_wage_income: 0,
    total_schedule_income: 0,
    total_schedule_income_with_losses: 0,
    total_other_income: 0,
    total_taxable_income: 0,
    tax_exempt_wage_income: 0,
    total_income: 0,
    tax_liability: 0,
    credit_for_other_city_tax_withheld: 0,
    final_balance: 0,
    final_balance_with_quarterly_estimated_payment: 0,
    net_estimated_taxes_owed: 0,
    quarterly_estimated_tax: 0,
    total_estimated_tax_due: 0,
    refund_amount: 0,
    tax_rate: 0.03,
    credit_for_springboro_tax_withheld: 0,
    taxpayer_credits: 0,
    total_credits: 0,
    other_city_tax_withheld: 0,
    one_percent_other_city_local_wages: 0,
};

const initialState = {
  taxCalculationsFetched: false,
  calculationFetchAttempts: 0,
  paymentChoice: PaymentOption.PayNow,
  status: HTTPRequestStatus.Idle,
  finalBalanceStatus: HTTPRequestStatus.Idle,
  paymentIntentStatus: HTTPRequestStatus.Idle,
  payLaterRequestStatus: HTTPRequestStatus.Idle,
  disableNext: false,
  refundUpdateStatus: HTTPRequestStatus.Idle,
  signatureStatus: HTTPRequestStatus.Idle,
  signatureUrl: "", 
  alertMessage: "",
  finalBalance: emptyTaxCalculations,
  refund: defaultRefund,
  paymentToken: "",
};

const getRefundType = (refund: number, credit: number): RefundType => {
  if (Math.abs(refund) > 0 && credit > 0) {
    return RefundType.Partial;
  } else if (Math.abs(refund) === 0 && credit > 0) {
    return RefundType.Credit;
  } else {
    return RefundType.Refund;
  }
}

export const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    finalBalanceRequest: (state) => {
      state.status = HTTPRequestStatus.Requesting;
      state.finalBalanceStatus = HTTPRequestStatus.Error;
      state.alertMessage = "";
      state.calculationFetchAttempts += 1;
    },
    paymentRequest: (
      state,
      action: PayloadAction<PaymentRequest>
    ) => {
      state.status = HTTPRequestStatus.Requesting;
      state.alertMessage = "";
    },
    fetchPaymentIntent: (
      state,
      action: PayloadAction<{taxReturnID: number, includesEstimatedPayment: boolean}>
    ) => {
      state.status = HTTPRequestStatus.Requesting;
      state.paymentIntentStatus = HTTPRequestStatus.Requesting;
      state.alertMessage = '';
    },
    fetchPaymentIntentError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.status = HTTPRequestStatus.Error;
      state.paymentIntentStatus = HTTPRequestStatus.Error;
      state.alertMessage = action.payload;
    },
    fetchPaymentIntentSuccess: (
      state,
      action: PayloadAction<PaymentIntentToken>
    ) => {
      state.status = HTTPRequestStatus.Success;
      state.paymentIntentStatus = HTTPRequestStatus.Success;
      state.paymentToken = action.payload.payment_intent_token;
    },
    payLaterRequest: (
      state,
      action: PayloadAction<PayLaterRequest>
    ) => {
      state.status = HTTPRequestStatus.Requesting;
      state.payLaterRequestStatus = HTTPRequestStatus.Requesting;
      state.alertMessage = '';
    },
    payLaterRequestError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.status = HTTPRequestStatus.Error;
      state.payLaterRequestStatus = HTTPRequestStatus.Error;
      state.alertMessage = action.payload;
    },
    payLaterRequestSuccess: (
      state
    ) => {
      state.status = HTTPRequestStatus.Success;
      state.payLaterRequestStatus = HTTPRequestStatus.Success;
    },
    changeSignature: (state, action: PayloadAction<string>) => {
      state.signatureUrl = action.payload;
    },
    signatureRequest: (state, action: PayloadAction<string>) => {
      state.alertMessage = '';
      state.signatureStatus = HTTPRequestStatus.Requesting;
      state.status = HTTPRequestStatus.Requesting;
    },
    signatureRequestSuccess: (state) => {
      state.signatureStatus = HTTPRequestStatus.Success;
      state.status = HTTPRequestStatus.Success;
    },
    signatureRequestError: (state, action: PayloadAction<string>) => {
      state.signatureStatus = HTTPRequestStatus.Error;
      state.status = HTTPRequestStatus.Error;
      state.alertMessage = action.payload;
    },
    finalBalanceError: (state, action: PayloadAction<string>) => {
      state.status = HTTPRequestStatus.Error;
      state.finalBalanceStatus = HTTPRequestStatus.Error;
      state.alertMessage = action.payload;
    },
    addAlertMessage: (state, action: PayloadAction<string>) => {
      state.alertMessage = action.payload;
    },
    resetAlertMessage: (state) => {
      state.alertMessage = '';
    },
    finalBalanceSuccess: (state, action: PayloadAction<TaxCalculations>) => {
      state.status = HTTPRequestStatus.Success;
      state.finalBalanceStatus = HTTPRequestStatus.Success;
      state.finalBalance = action.payload;
      state.taxCalculationsFetched = true;

      const { refund_amount } = action.payload;
      state.refund.refund_amount = refund_amount;
      state.refund.credit_amount = 0;
    },
    resetStatus: (state) => {
      state.alertMessage = '';
      state.status = HTTPRequestStatus.Idle;
      state.finalBalanceStatus = HTTPRequestStatus.Idle;
      state.paymentIntentStatus = HTTPRequestStatus.Idle;
      state.refundUpdateStatus = HTTPRequestStatus.Idle;
    },
    updateRefund: (
      state,
      action: PayloadAction<Refund>
    ) => {
      state.status = HTTPRequestStatus.Requesting;
      state.alertMessage = "";
      state.refundUpdateStatus = HTTPRequestStatus.Requesting;
      // TODO double check action contents
      //state.refund = action.payload;
    },
    updateRefundSuccess: (state) => {
      state.status = HTTPRequestStatus.Success;
      state.refundUpdateStatus = HTTPRequestStatus.Success;
      state.alertMessage = 'Your refund was successfully saved';
    },
    updateRefundError: (state, action: PayloadAction<string>) => {
      state.status = HTTPRequestStatus.Error;
      state.refundUpdateStatus = HTTPRequestStatus.Error;
      state.alertMessage = action.payload;
    },
    changeRefund: (
      state,
      action: PayloadAction<{refundAmount: number, creditAmount: number}>
    ) => {
      const refund: Refund = {
        type: getRefundType(Number(toIntString(action.payload.refundAmount)), action.payload.creditAmount),
        refund_amount: action.payload.refundAmount,
        credit_amount: action.payload.creditAmount,
      };
      state.refund = refund;
    },
    confirmStep: (_) => {},
    confirmRefund: (_) => {},
    confirmSignature: (
      state,
      action: PayloadAction<{signatureRef: RefObject<SignatureCanvas | null>, acceptReturn: boolean}>
    ) => {},
    setErrorAlertMessage: (
      state,
      action: PayloadAction<string>
    ) => {
      state.status = HTTPRequestStatus.Error; // TODO this is kinda hacky
      state.alertMessage =  action.payload;
    },
    setPaymentChoice: (
      state,
      action: PayloadAction<PaymentOption>
    ) => {
      state.paymentChoice =  action.payload;
    },

  },
});
